import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Paper,
  Code,
} from "@mantine/core";
import { IconAlertTriangle, IconRefresh, IconHome } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

interface PageErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

/**
 * Fallback UI displayed when an error is caught by the ErrorBoundary.
 * Provides options to retry or navigate to the dashboard.
 */
function PageErrorFallback({ error, onReset }: PageErrorFallbackProps) {
  const navigate = useNavigate();
  const isDevelopment = process.env.NODE_ENV === "development";

  const handleGoToDashboard = () => {
    onReset();
    navigate("/dashboard");
  };

  return (
    <Container size="sm" py="xl">
      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          <IconAlertTriangle
            size={64}
            color="var(--mantine-color-red-6)"
            stroke={1.5}
          />

          <Stack align="center" gap="xs">
            <Title order={2} ta="center">
              Something went wrong
            </Title>
            <Text c="dimmed" ta="center" size="sm">
              An unexpected error occurred while loading this page. You can try
              again or return to the dashboard.
            </Text>
          </Stack>

          {isDevelopment && error && (
            <Paper bg="gray.1" p="md" radius="sm" w="100%">
              <Stack gap="xs">
                <Text size="sm" fw={600} c="red">
                  {error.name}: {error.message}
                </Text>
                {error.stack && (
                  <Code
                    block
                    style={{
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                  >
                    {error.stack}
                  </Code>
                )}
              </Stack>
            </Paper>
          )}

          <Group>
            <Button
              variant="filled"
              leftSection={<IconRefresh size={18} />}
              onClick={onReset}
            >
              Try Again
            </Button>
            <Button
              variant="light"
              leftSection={<IconHome size={18} />}
              onClick={handleGoToDashboard}
            >
              Go to Dashboard
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
}

export { PageErrorFallback };
export type { PageErrorFallbackProps };
